import { ReactElement, useCallback } from 'react';

import type { IColumnDefinitionType } from 'interfaces/Table/DataTableColumn.interface';
import type { IId } from 'interfaces/IId.interface';
import { TruncatedCellStyled } from 'components/Table/components/TableBody/TableBody.styled';
import {
  StyledResultInnerRow,
  StyledResultRow,
  StyledRowCell,
} from './ResultRow.styled';
import { getGridColsWidth } from 'components/Table/components/util';

interface IResultRowProps<T extends IId> {
  row: T;
  columns: IColumnDefinitionType<T>[];
  onClick: () => void;
}

export const ResultRow = <T extends IId>({
  row,
  columns,
  onClick,
}: IResultRowProps<T>): ReactElement => {
  const renderCell = useCallback(
    (column: IColumnDefinitionType<T>) => {
      const getCell = () => {
        if (column.cellType === 'custom' && column.cellRenderer) {
          return column.cellRenderer(row, false);
        }

        return (
          <TruncatedCellStyled>
            {(row[column.key as keyof T] as unknown as string) || '-'}
          </TruncatedCellStyled>
        );
      };

      return (
        <StyledRowCell
          $isFirst={column.key === 'status'}
          key={`cell-${row.id}-${column.key}`}
          $disabled={false}
        >
          {getCell()}
        </StyledRowCell>
      );
    },

    [row]
  );

  const checkIfColumnShouldBeRendered = useCallback(
    (column: IColumnDefinitionType<T>) => {
      return column.visibility;
    },
    []
  );

  return (
    <StyledResultRow key={`inner-row-${row.id}`} onClick={onClick}>
      <StyledResultInnerRow $gridColsWidth={getGridColsWidth(columns)}>
        {columns.map((column) => {
          return checkIfColumnShouldBeRendered(column)
            ? renderCell(column)
            : null;
        })}
      </StyledResultInnerRow>
    </StyledResultRow>
  );
};
